import {
	BenchmarkIds,
	UserEnhancementExposureColumnPreferencePreferenceTypeEnum as ColType,
	InvestmentEnhancementExportControllerApiFactory,
	type InvestmentExposureEntry,
	type InvestmentExposureResponse,
} from "@eu.mdotm.api/sphere";
import {
	AutoSortHScrollTableWithGroups,
	Row,
	TableDataCell,
	type TableWithGroupsColumn,
	type TableWithGroupsProps,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSort, builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { groupBy } from "@mdotm/utils/collections";
import { useQuery } from "@tanstack/react-query";
import { Map } from "immutable";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiGen } from "$root/api/hooks";
import barchart_texture from "$root/assets/images/barchart-texture.png";
import MonitorWithHourglass from "$root/assets/images/glyphs[preload]/MonitorWithHourglass.svg";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import GraphLegend from "$root/components/GraphLegend";
import { IconWallBase, IconWalls } from "$root/components/IconWall";
import { InfoDelta } from "$root/components/InfoDelta";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { currencyCode } from "$root/utils/currency";
import {
	PortfolioQueryWidgetBase,
	portfolioWidgetMissingDataReason,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedQuery, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { BarGraphPCSvg } from "$root/ui-lib/charts";
import { groupActionsColumn, settingsColumnName } from "$root/ui-lib/interactive-collections/common-table-actions";
import { type ColumnMetadata, spawnColumnChooser } from "$root/ui-lib/table/extra/ColumnChooser";
import {
	type ContextContent,
	colorGenerator,
	downloadContentDisposition,
	getGraphMarkers,
	qualifier,
	roundCustomByStep,
	useTaskQueueWithAtom,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type ExposureCategories,
	type ExposureCategoryState,
	ExposureCategorySelector,
	riskModelExposureCategories,
	WithExposureCategories,
} from "../ExposureEvolve";
import type { InvestmentExposureResponseExposureTypeEnum } from "../ExposureEvolve/InvestmentExposureResponseExposureTypeEnum";
import { addMarketValue, hasMarketValue } from "../ExposureEvolve/marketValue";

const useColumnLabels = (proposalDate?: string) => {
	const { formatDate } = useLocaleFormatters();
	return {
		[ColType.Current]: `Composition\n${formatDate(proposalDate, { omitYear: true })}`,
		[ColType.Proposal]: "Proposal",
		[ColType.Reference]: "Reference",
		[ColType.Benchmark]: "Benchmark",
		[ColType.DifferenceOnCurrent]: "Delta",
		[ColType.DifferenceOnReference]: "Delta on reference",
		[ColType.DifferenceOnBenchmark]: "Delta on benchmark",
	} as const;
};

/**
 * @internal — exported for tests
 * Folds the four exposure compositions (enhanced / current / reference / benchmark) into the grouped
 * table model the widget renders: one entry per `firstQualityLevel`, its per-kind weight sums, and the
 * per-`secondQualityLevel` rows with each kind's weight merged in. Returns `null` before data arrives,
 * `[]` when the two mandatory compositions are missing, otherwise groups sorted by enhanced sum desc.
 */
export function buildExposureTableData(data: InvestmentExposureResponse | null | undefined) {
	if (!data) {
		return null;
	}
	const { enhancementComposition, investmentComposition, referenceComposition, benchmarkComposition } = data;

	if (!enhancementComposition || !investmentComposition) {
		return [];
	}

	const compostion = [
		...enhancementComposition.map((x) => ({ ...x, kind: "enhanced" as const })),
		...investmentComposition.map((x) => ({ ...x, kind: "current" as const })),
		...(referenceComposition?.map((x) => ({
			...x,
			kind: "reference" as const,
		})) ?? []),
		...(benchmarkComposition?.map((x) => ({
			...x,
			kind: "benchmark" as const,
		})) ?? []),
	];

	const mappedComposition = Map(groupBy(compostion, (comp) => comp.firstQualityLevel!));

	return Array.from(mappedComposition.keys())
		.map((firstQualityLevel, i) => {
			const compositionEntries = mappedComposition.get(firstQualityLevel);
			const sum = (compositionEntries ?? []).reduce(
				(acc, x) => {
					switch (x.kind) {
						case "current":
							acc.currentSum += x.weight ?? 0;
							break;
						case "enhanced":
							acc.enhancedSum += x.weight ?? 0;
							break;
						case "reference":
							acc.referenceSum += x.weight ?? 0;
							break;
						case "benchmark":
							acc.benchmarkSum += x.weight ?? 0;
							break;
					}
					return acc;
				},
				{ enhancedSum: 0, currentSum: 0, referenceSum: 0, benchmarkSum: 0 },
			);

			return {
				label: firstQualityLevel,
				enhancedSum: sum.enhancedSum,
				currentSum: sum.currentSum,
				referenceSum: sum.referenceSum,
				benchmarkSum: sum.benchmarkSum,
				groupIndex: i,
				rows: Object.values(
					(compositionEntries ?? []).reduce<{
						[key: string]: {
							enhancedWeight: number;
							currentWeight: number;
							referenceWeight: number;
							benchmarkWeight: number;
							label: string;
							netLong: boolean;
						};
					}>((acc, x) => {
						if (x.firstQualityLevel === x.secondQualityLevel) {
							return acc;
						}

						if (!acc[x.secondQualityLevel!]) {
							acc[x.secondQualityLevel!] = {
								enhancedWeight: x.kind === "enhanced" ? (x.weight ?? 0) : 0,
								currentWeight: x.kind === "current" ? (x.weight ?? 0) : 0,
								referenceWeight: x.kind === "reference" ? (x.weight ?? 0) : 0,
								benchmarkWeight: x.kind === "benchmark" ? (x.weight ?? 0) : 0,
								label: x.secondQualityLevel ?? "-",
								netLong: x.netLong ?? false,
							};
							return acc;
						}

						acc[x.secondQualityLevel!] = {
							...acc[x.secondQualityLevel!],
							enhancedWeight: x.kind === "enhanced" ? (x.weight ?? 0) : acc[x.secondQualityLevel!].enhancedWeight,
							currentWeight: x.kind === "current" ? (x.weight ?? 0) : acc[x.secondQualityLevel!].currentWeight,
							referenceWeight: x.kind === "reference" ? (x.weight ?? 0) : acc[x.secondQualityLevel!].referenceWeight,
							benchmarkWeight: x.kind === "benchmark" ? (x.weight ?? 0) : acc[x.secondQualityLevel!].benchmarkWeight,
						};
						return acc;
					}, {}),
				),
			};
		})
		.sort((a, b) => (a.enhancedSum > b.enhancedSum ? -1 : 1));
}

/**
 * @internal — exported for tests
 * Chart scale for the exposure bars: the largest per-group total of enhanced or current row weights,
 * rounded up to the nearest step of 10. `null` when there is no table data yet.
 */
export function computeExposureGraphLimits(tableData: ReturnType<typeof buildExposureTableData>) {
	if (!tableData) {
		return null;
	}
	const value = tableData.reduce((acc, group) => {
		const rowValues = group.rows.reduce(
			(a, row) => {
				return {
					value: a.value + row.enhancedWeight,
					previousValue: a.previousValue + row.currentWeight,
				};
			},
			{ value: 0, previousValue: 0 },
		);
		return Math.max(acc, rowValues.value, rowValues.previousValue);
	}, 0);
	return roundCustomByStep(value, 10);
}

/**
 * @internal — exported for tests
 * The current composition's market value (`InvestmentExposureEntry.value`, filled for quantity
 * portfolios only) summed per group (`firstQualityLevel`), cent-rounded.
 */
export function currentMarketValueByGroup(
	composition: ReadonlyArray<InvestmentExposureEntry> | undefined,
): Partial<Record<string, number>> {
	const byGroup: Partial<Record<string, number>> = {};
	for (const entry of composition ?? []) {
		const group = entry.firstQualityLevel ?? "";
		byGroup[group] = addMarketValue(byGroup[group], entry.value);
	}
	return byGroup;
}

const ExposureCompare = (ctx: ContextContent<typeof PortfolioContext>) => {
	const { portfolio, selectedBenchmark } = ctx;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier;

	const exposureAvailableQuery = useQueryNoRefetch(
		["queryExposureLevelEvolve", "enhance", portfolio?.uuid, benchmarkId, ctx.portfolio],
		{
			queryFn: () => {
				if (!portfolio?.uuid || !benchmarkId) {
					return {
						data: undefined,
						widgetStatus: portfolioWidgetMissingDataReason(ctx.portfolio, "ExanteContributionVolatility"),
					};
				}

				return {
					data: {},
					widgetStatus: WidgetStatus.READY,
				};
			},
		},
	);

	return (
		<PortfolioQueryWidgetBase query={exposureAvailableQuery}>
			{() => (
				<WithExposureCategories>
					{(exposureCategories) => (
						<ExposureCompareInner exposureCategories={exposureCategories} ctx={ctx} benchmarkId={benchmarkId} />
					)}
				</WithExposureCategories>
			)}
		</PortfolioQueryWidgetBase>
	);
};

const staticColumns = ["MACRO_ASSET_CLASS", "CURRENT", "PROPOSAL", "DIFFERENCE_ON_CURRENT"] satisfies Array<ColType>;
const ExposureCompareInner = ({
	ctx,
	exposureCategories,
	benchmarkId,
}: {
	ctx: ContextContent<typeof PortfolioContext>;
	exposureCategories: ExposureCategories;
	benchmarkId: string | undefined;
}) => {
	const { portfolio } = ctx;

	const [exposureCategory, setExposureCategory] = useState<ExposureCategoryState>({
		firstLevel: "RISK_MODEL",
		secondLevel: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS" satisfies InvestmentExposureResponseExposureTypeEnum,
	});

	const query = useQuery({
		...portfolioRepository.exposure.getQueryConfig(portfolio?.uuid ?? "", {
			benchmarkId: benchmarkId ?? null,
			secondLevel: exposureCategory.secondLevel,
			enhanced: true,
		}),
		keepPreviousData: true,
	});

	const { t } = useTranslation();
	const exportApi = useApiGen(InvestmentEnhancementExportControllerApiFactory);

	const exportPortfolioExposure = useCallback(
		async (cat: InvestmentExposureResponseExposureTypeEnum) => {
			const response = await exportApi.exportExposure1(portfolio.uuid, cat, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, portfolio],
	);

	const { formatNumber, formatDate } = useLocaleFormatters();
	const columnLabels = useColumnLabels(portfolio.modificationDate);

	const columnsMetadataQueryRaw = instrumentsRepository.userEnhancementExposureColumnPreferences.useQuery();
	const columnsMetadataQuery = useMappedQuery(
		(raw) =>
			(raw.userEnhancementExposureColumnPreferences ?? []).flatMap<ColumnMetadata<ColType>>((x) =>
				(staticColumns as Array<ColType>).includes(x.preferenceType!)
					? []
					: [
							{
								id: x.preferenceType!,
								checked: x.enabled ?? true,
								label: columnLabels[x.preferenceType! as Exclude<ColType, (typeof staticColumns)[number]>],
							},
						],
			),
		[columnLabels],
		columnsMetadataQueryRaw,
	);

	useWidgetOptions(
		() => ({
			title: (
				<Text
					type="Body/XL/Bold"
					title={t("EXPOSURE.TITLE")}
					className="truncate"
					data-qualifier={qualifier.widgets.portfolioExposureCompare.name}
				>
					{t("EXPOSURE.TITLE")}
				</Text>
			),
			isLoading: query.isFetching,
			tooltip: t("EXPOSURE.TOOLTIP"),
			actionHeader: !(query.isError || query.isFetching) ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{
						"data-qualifier": qualifier.component.dropdown.trigger("ExposureCompare/Download"),
					}}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportPortfolioExposure(
									exposureCategory.secondLevel as InvestmentExposureResponseExposureTypeEnum,
								);
							},
							label: t("EXPOSURE.DOWNLOAD_TITLE"),
							disabled: exposureCategory.secondLevel
								? !riskModelExposureCategories.some((x) => x.value === exposureCategory.secondLevel)
								: true,
							"data-qualifier": qualifier.component.dropdown.item("Exposure"),
						},
					]}
				/>
			) : null,
		}),
		[exposureCategory.secondLevel, exportPortfolioExposure, query.isError, query.isFetching, t],
	);

	const isLegacyBenchmark = useMemo(
		() => (Object.values(BenchmarkIds) as Array<string>).includes(benchmarkId ?? ""),
		[benchmarkId],
	);

	const showBenchmark = useMemo(
		() =>
			(columnsMetadataQuery.data?.some(
				(col) => (col.id === ColType.Benchmark || col.id === ColType.DifferenceOnBenchmark) && col.checked,
			) ??
				false) &&
			!isLegacyBenchmark,
		[columnsMetadataQuery.data, isLegacyBenchmark],
	);
	const showReference = useMemo(
		() =>
			columnsMetadataQuery.data?.some(
				(col) => (col.id === ColType.Reference || col.id === ColType.DifferenceOnReference) && col.checked,
			) ?? false,
		[columnsMetadataQuery.data],
	);

	const tableData = useMemo(() => buildExposureTableData(query.data), [query.data]);

	const marketValueByGroup = useMemo(
		() =>
			hasMarketValue(exposureCategory.firstLevel, query.data?.investmentComposition)
				? currentMarketValueByGroup(query.data?.investmentComposition)
				: undefined,
		[exposureCategory.firstLevel, query.data],
	);

	// Correction Of Graph Scala
	const exposureGraphLimits = useMemo(() => computeExposureGraphLimits(tableData), [tableData]);

	const graphColumnMarkers = useMemo(
		() => (exposureGraphLimits != null ? getGraphMarkers(exposureGraphLimits, 0, 10, "%", 6) : null),
		[exposureGraphLimits],
	);

	const {
		name: nameCol,
		CURRENT: currentCol,
		PROPOSAL: proposalCol,
		DIFFERENCE_ON_CURRENT: differenceOnCurrentCol,
		marketValue: marketValueCol,
		graph: graphCol,
		...customizableColumns
	} = useMemo(
		() =>
			({
				name: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: "name" as const,
					header: (exposureCategories.fromRiskModel.find((cat) => cat.value === exposureCategory.secondLevel) ??
						exposureCategories.fromTagClassifications.find((cat) => cat.value === exposureCategory.secondLevel))!
						.columnTitle,
					groupContent: (groupedRow) => groupedRow.label,
					content: (row, cellProps) => (
						<TableDataCell {...cellProps} title={row.label}>
							{row.label}{" "}
							{!row.netLong && (
								<>
									-{" "}
									<span className="whitespace-nowrap">
										[{" "}
										<svg
											className="inline w-[8px] mb-1"
											width="8"
											height="8"
											viewBox="0 0 8 8"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<title>band</title>
											<rect opacity="0.3" width="8" height="8" rx="4" fill={colorGenerator(1)} />
										</svg>{" "}
										<span className="truncate">Net Short</span> ]
									</span>
								</>
							)}
						</TableDataCell>
					),
					sortFn: builtInSortFnFor("label"),
					//relativeWidth: 2,
					minWidth: 240,
					maxWidth: 240,
				}),
				[ColType.Current]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.Current,
					headerCellClassName: "whitespace-pre-line",
					header: columnLabels[ColType.Current],
					groupContentTextType: "Body/M/Medium",
					groupCellClassName: "tabular-nums",
					groupContent: (groupedRow) => `${formatNumber(groupedRow.currentSum, 2)}%`,
					cellClassName: "tabular-nums",
					content: (row) => `${formatNumber(row.currentWeight, 2)}%`,
					sortFn: builtInSortFnFor("currentSum"),
					//relativeWidth: 1,
					align: "end",
					minWidth: 140,
					maxWidth: 140,
				}),
				[ColType.Proposal]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.Proposal,
					header: columnLabels[ColType.Proposal],
					groupContentTextType: "Body/M/Medium",
					groupCellClassName: "tabular-nums",
					groupContent: (groupedRow) => `${formatNumber(groupedRow.enhancedSum, 2)}%`,
					cellClassName: "tabular-nums",
					content: (row) => `${formatNumber(row.enhancedWeight, 2)}%`,
					sortFn: builtInSortFnFor("enhancedSum"),
					//relativeWidth: 1,
					align: "end",
					minWidth: 140,
					maxWidth: 140,
				}),
				[ColType.Reference]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.Reference,
					header: columnLabels[ColType.Reference],
					groupContentTextType: "Body/M/Medium",
					groupCellClassName: "tabular-nums",
					groupContent: (groupedRow) => `${formatNumber(groupedRow.referenceSum, 2)}%`,
					cellClassName: "tabular-nums",
					content: (row) => `${formatNumber(row.referenceWeight, 2)}%`,
					sortFn: builtInSortFnFor("referenceSum"),
					//relativeWidth: 1,
					align: "end",
					minWidth: 140,
					maxWidth: 140,
				}),
				[ColType.Benchmark]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.Benchmark,
					header: columnLabels[ColType.Benchmark],
					groupContentTextType: "Body/M/Medium",
					groupCellClassName: "tabular-nums",
					groupContent: (groupedRow) => (isLegacyBenchmark ? null : `${formatNumber(groupedRow.benchmarkSum, 2)}%`),
					cellClassName: "tabular-nums",
					content: (row) => (isLegacyBenchmark ? null : `${formatNumber(row.benchmarkWeight, 2)}%`),
					sortFn: builtInSortFnFor("benchmarkSum"),
					//relativeWidth: 1,
					align: "end",
					minWidth: 140,
					maxWidth: 140,
				}),
				graph: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: "graph" as const,
					orderable: false,
					header: (headerProps) => (
						<Row {...headerProps} justifyContent="space-between" alignItems="center">
							{graphColumnMarkers?.map((m, i) => (
								<Text as="div" key={`marker-${i}`} type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
									{m.label}
								</Text>
							))}
						</Row>
					),
					groupContent: ({ currentSum, enhancedSum, benchmarkSum, referenceSum }, cellProps) => {
						return (
							<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
								<BarGraphPCSvg
									className="w-full"
									options={{
										resize: true,
										marksLabels: false,
										scale: { max: exposureGraphLimits ?? 100, min: 0 },
										bars: { height: 6, gap: 2 },
										vPadding: 2,
									}}
									data={[
										{ value: currentSum, color: themeCSSVars.palette_B500 },
										{ value: enhancedSum, color: themeCSSVars.palette_S500 },
										...(showBenchmark
											? [
													{
														value: benchmarkSum ?? 0,
														color: themeCSSVars.palette_N200,
													},
												]
											: []),
										...(showReference ? [{ value: referenceSum ?? 0, color: "texture" }] : []),
									]}
								/>
							</Row>
						);
					},
					content: ({ currentWeight, enhancedWeight, benchmarkWeight, referenceWeight }, cellProps) => {
						return (
							<Row justifyContent="center" {...cellProps} childrenGrow={1} alignItems="center">
								<BarGraphPCSvg
									className="w-full"
									options={{
										resize: true,
										marksLabels: false,
										scale: { max: exposureGraphLimits ?? 100, min: 0 },
										bars: { height: 6, gap: 2 },
										vPadding: 2,
									}}
									data={[
										{ value: currentWeight, color: themeCSSVars.palette_B500 },
										{ value: enhancedWeight, color: themeCSSVars.palette_S500 },
										...(showBenchmark
											? [
													{
														value: benchmarkWeight ?? 0,
														color: themeCSSVars.palette_N200,
													},
												]
											: []),
										...(showReference ? [{ value: referenceWeight ?? 0, color: "texture" }] : []),
									]}
								/>
							</Row>
						);
					},
					//relativeWidth: 4,
					minWidth: 180,
				}),
				[ColType.DifferenceOnCurrent]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.DifferenceOnCurrent,
					header: columnLabels[ColType.DifferenceOnCurrent],
					align: "end",
					groupContentTextType: "Body/M/Medium",
					groupContent: ({ currentSum, enhancedSum }, { textType, style, ...cellProps }) => (
						<Row
							style={{ ...style, color: themeCSSVars.categorical_text_primary }}
							data-text-type={textType}
							{...cellProps}
							justifyContent="stretch"
							alignItems="center"
						>
							<InfoDelta diff={(enhancedSum ?? 0) - (currentSum ?? 0)} enh={enhancedSum ?? 0} />
						</Row>
					),
					content: ({ currentWeight, enhancedWeight }, { textType, style, ...cellProps }) => (
						<Row
							style={{ ...style, color: themeCSSVars.categorical_text_primary }}
							data-text-type={textType}
							{...cellProps}
							justifyContent="stretch"
							alignItems="center"
						>
							<InfoDelta diff={(enhancedWeight ?? 0) - (currentWeight ?? 0)} enh={enhancedWeight ?? 0} />
						</Row>
					),
					minWidth: 125,
					maxWidth: 125,
					//relativeWidth: 1,
					sortFn: (a, b) => {
						const deltaA = a.enhancedSum - a.currentSum;
						const deltaB = b.enhancedSum - b.currentSum;

						return builtInSort(deltaA, deltaB);
					},
				}),
				marketValue: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: "marketValue" as const,
					header: `${t("EXPOSURE.MONETARY_VALUE")} (${currencyCode(portfolio.baseCurrency)})`,
					groupContentTextType: "Body/M/Medium",
					groupCellClassName: "tabular-nums",
					groupContent: (groupedRow) => {
						const value = marketValueByGroup?.[groupedRow.label];
						return value === undefined ? null : formatNumber(value, 2);
					},
					// A tag breakdown has one level, so its groups carry no rows.
					content: () => null,
					sortFn: (a, b) => builtInSort(marketValueByGroup?.[a.label], marketValueByGroup?.[b.label]),
					align: "end",
					minWidth: 140,
					maxWidth: 140,
				}),
				[ColType.DifferenceOnReference]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.DifferenceOnReference,
					header: columnLabels[ColType.DifferenceOnReference],
					align: "end",
					groupContentTextType: "Body/M/Medium",
					groupContent: ({ enhancedSum, referenceSum }, { textType, style, ...cellProps }) => (
						<Row
							style={{ ...style, color: themeCSSVars.categorical_text_primary }}
							data-text-type={textType}
							{...cellProps}
							justifyContent="stretch"
							alignItems="center"
						>
							<InfoDelta diff={(enhancedSum ?? 0) - (referenceSum ?? 0)} enh={enhancedSum ?? 0} zeroAsRemoved={false} />
						</Row>
					),
					content: ({ enhancedWeight, referenceWeight }, { textType, style, ...cellProps }) => (
						<Row
							style={{ ...style, color: themeCSSVars.categorical_text_primary }}
							data-text-type={textType}
							{...cellProps}
							justifyContent="stretch"
							alignItems="center"
						>
							<InfoDelta
								diff={(enhancedWeight ?? 0) - (referenceWeight ?? 0)}
								enh={enhancedWeight ?? 0}
								zeroAsRemoved={false}
							/>
						</Row>
					),
					minWidth: 125,
					maxWidth: 125,
					//relativeWidth: 1,
					sortFn: (a, b) => {
						const deltaA = a.enhancedSum - a.referenceSum;
						const deltaB = b.enhancedSum - b.referenceSum;

						return builtInSort(deltaA, deltaB);
					},
				}),
				[ColType.DifferenceOnBenchmark]: (): TableWithGroupsColumn<NonNullable<typeof tableData>[number]> => ({
					name: ColType.DifferenceOnBenchmark,
					header: columnLabels[ColType.DifferenceOnBenchmark],
					align: "end",
					groupContentTextType: "Body/M/Medium",
					groupContent: ({ enhancedSum, benchmarkSum }, { textType, style, ...cellProps }) =>
						isLegacyBenchmark ? null : (
							<Row
								style={{
									...style,
									color: themeCSSVars.categorical_text_primary,
								}}
								data-text-type={textType}
								{...cellProps}
								justifyContent="stretch"
								alignItems="center"
							>
								<InfoDelta
									diff={(enhancedSum ?? 0) - (benchmarkSum ?? 0)}
									enh={enhancedSum ?? 0}
									zeroAsRemoved={false}
								/>
							</Row>
						),
					content: ({ enhancedWeight, benchmarkWeight }, { textType, style, ...cellProps }) =>
						isLegacyBenchmark ? null : (
							<Row
								style={{
									...style,
									color: themeCSSVars.categorical_text_primary,
								}}
								data-text-type={textType}
								{...cellProps}
								justifyContent="stretch"
								alignItems="center"
							>
								<InfoDelta
									diff={(enhancedWeight ?? 0) - (benchmarkWeight ?? 0)}
									enh={enhancedWeight ?? 0}
									zeroAsRemoved={false}
								/>
							</Row>
						),
					minWidth: 125,
					maxWidth: 125,
					//relativeWidth: 1,
					sortFn: (a, b) => {
						const deltaA = a.enhancedSum - a.benchmarkSum;
						const deltaB = b.enhancedSum - b.benchmarkSum;

						return builtInSort(deltaA, deltaB);
					},
				}),
			}) as const,
		[
			columnLabels,
			exposureCategories.fromRiskModel,
			exposureCategories.fromTagClassifications,
			exposureCategory.secondLevel,
			exposureGraphLimits,
			formatNumber,
			graphColumnMarkers,
			isLegacyBenchmark,
			marketValueByGroup,
			portfolio.baseCurrency,
			showBenchmark,
			showReference,
			t,
		],
	);

	const [, columnTaskQueue] = useTaskQueueWithAtom();

	return (
		<div className="flex flex-col min-h-0 grow">
			<ExposureCategorySelector
				className="max-w-2/4 grow-0!"
				exposureCategory={exposureCategory}
				setExposureCategory={setExposureCategory}
				exposureCategories={exposureCategories}
			/>
			{query.data != null || query.isLoading ? (
				query.data && (!query.data.enhancementComposition || !query.data.investmentComposition) ? (
					<div className="w-full grow min-h-0 pt-2">
						<IconWalls.CalculatingData />
					</div>
				) : (
					<>
						<div
							className={overrideClassName("grow min-h-0 w-full pb-3 mt-3 mb-2 flex flex-col items-stretch", {
								"opacity-60": query.isFetching,
							})}
						>
							{tableData && (
								<ReactQueryWrapperBase query={columnsMetadataQuery}>
									{(columnsMetadata) => {
										const columns: TableWithGroupsProps<NonNullable<typeof tableData>[number]>["columns"] = ({
											expandColumn,
										}) => [
											expandColumn,
											nameCol(),
											currentCol(),
											proposalCol(),
											differenceOnCurrentCol(),
											...(marketValueByGroup ? [marketValueCol()] : []),
											...columnsMetadata
												.filter((x) => x.checked)
												.map((x) => customizableColumns[x.id as Exclude<ColType, (typeof staticColumns)[number]>]()),
											graphCol(),
											groupActionsColumn({
												onSettingsClick: () =>
													spawnColumnChooser({
														columns: columnsMetadata,
														onChange(cols) {
															columnTaskQueue.enqueue(() =>
																instrumentsRepository.setUserExposureEnhancementColumnPreferences(
																	cols.map((c) => ({
																		preferenceType: c.id,
																		enabled: c.checked,
																	})),
																),
															);
														},
													}),
												dropdownActions: [],
											}),
										];

										return (
											<AutoSortHScrollTableWithGroups
												pinnedColumns={[
													{
														name: "name",
														side: "left",
													},
													{
														name: settingsColumnName,
														side: "right",
													},
												]}
												groupedRows={tableData}
												groupRowKey={(group) => group.label}
												columns={columns}
											/>
										);
									}}
								</ReactQueryWrapperBase>
							)}
						</div>
						<GraphLegend className="space-x-3">
							<div className="legend-container light book">
								<ColoredRectangle color={themeCSSVars.palette_graph_B500} variant="vertical" />{" "}
								{`Composition ${formatDate(ctx.portfolio.modificationDate, { omitYear: true })}`}
							</div>
							<div className="legend-container light book">
								<ColoredRectangle color={themeCSSVars.palette_S500} variant="vertical" /> Proposal
							</div>
							{showBenchmark && (
								<div className="legend-container light book">
									<ColoredRectangle color={themeCSSVars.palette_N200} variant="vertical" /> Benchmark
								</div>
							)}
							{showReference && (
								<div className="legend-container light book">
									<ColoredRectangle texture={barchart_texture} width={6} height={6} variant="vertical" /> Reference
								</div>
							)}
						</GraphLegend>
					</>
				)
			) : (
				<div className="w-full grow min-h-0 pt-2">
					<IconWallBase>
						<div className="flex-1 min-h-0 flex flex-col">
							<img alt="illustration" src={MonitorWithHourglass} className="mx-auto my-0" />
							<Text as="div" className="px-1 text-center" type="Body/M/Book">
								Data not available for the selected comparison, please pick another option.
							</Text>
						</div>
					</IconWallBase>
				</div>
			)}
		</div>
	);
};

export default withContext(PortfolioContext)(ExposureCompare);
